import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { CopyButton } from '@ui/buttons';
import { jsonLines } from './jsonLines';
import { Consequence, ModeToggle } from './styles';

// The whole spec as JSON, at the bottom of the page it belongs to.
//
// Every other surface in this feature shows the spec through something: four question panels, four
// scoped `{ }` panes, a contract rail. All of them are lossy on purpose. This is the one place that
// shows the document itself, in one piece, and the reason it earns its space is that the document
// is what the compiler reads — a review before locking, a paste into a ticket, or a diff against
// what the API actually stored all want the bytes, not the summary.
//
// What "nicely" means here, concretely, and why each part is not decoration:
//
//   * Folding. A real spec runs several hundred lines and most of them are one `output_schema`. A
//     listing you have to scroll past to reach `score_gen` is a listing nobody reads twice.
//   * Line numbers. The problems strip and the validator both talk in paths, but a review comment
//     says "line 212" — and the numbers stay honest across a fold, showing the gap.
//   * Wrapping, on by default. A prompt is one JSON string with the whole instruction inside it, so
//     the interesting line is routinely 2,000 characters. Off, it is one horizontal scrollbar.
//   * Named views instead of one dump, because on a draft there is more than one honest answer to
//     "the JSON of this spec" — see the callers.
//
// The listing is generated from the value rather than regexed out of printed text; jsonLines.js
// says why, and holds the round trip to `JSON.stringify(value, null, 2)` exactly.

// ---- syntax palette ----
//
// Literals, not theme tokens, and deliberately so. The semantic colours this feature uses elsewhere
// are signal colours tuned to shout on a status pill — `positive` is #1AD36F, which on the light
// theme's white is close to unreadable as 12px body text, and `primaryCtaButton` is charcoal on one
// theme and yellow on the other. A code listing needs five hues that are all comfortably legible at
// the same size against the SAME background, which is a different problem from "is this row good".
//
// Which set to use is read off the theme's own text colour rather than off the theme's name, so the
// custom theme — any 29 colours an operator picked — lands on the right one too.
const LIGHT_SYNTAX = {
  key: '#1d4f91',
  string: '#0a7040',
  number: '#8a4b00',
  literal: '#7b2d8e',
  punct: 'rgba(31, 31, 31, 0.45)',
};

const DARK_SYNTAX = {
  key: '#8ab6ff',
  string: '#79dd9a',
  number: '#ffbe6f',
  literal: '#d9a3ff',
  punct: 'rgba(255, 255, 255, 0.45)',
};

const parseColor = (input) => {
  const raw = String(input == null ? '' : input).trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(raw);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
    return [parseInt(digits.slice(0, 2), 16), parseInt(digits.slice(2, 4), 16), parseInt(digits.slice(4, 6), 16)];
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(raw);
  if (rgb) {
    const parts = rgb[1].split(',').map((part) => Number(part.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) return parts.slice(0, 3);
  }
  return null;
};

// True when the theme's FOREGROUND is light, which is the only reliable read on whether the surface
// under it is dark. Unparseable (a named colour, a gradient) falls back to the light set, which is
// the app's default theme.
export const isDarkSurface = (textColor) => {
  const rgb = parseColor(textColor);
  if (!rgb) return false;
  return (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255 > 0.5;
};

const syntaxFor = (theme) => (isDarkSurface(theme.themeColors.text) ? DARK_SYNTAX : LIGHT_SYNTAX);

// ---- chrome ----

// `lowContrastBorder`, not `border`: nothing in here changes the spec. The feature's convention is
// that an interactive surface takes the stronger token and a panel that only tells you something
// takes the weaker one.
const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 7px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  overflow: hidden;
`;

const Head = styled.button`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 14px 16px;
  color: ${({ theme }) => theme.themeColors.text};

  &:hover {
    background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -2px;
  }
`;

const Caret = styled.span`
  flex: 0 0 auto;
  font-size: 11px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.16s ease;
`;

const HeadText = styled.span`
  flex: 1;
  min-width: 0;
`;

const Bodywrap = styled.div`
  padding: 0 16px 16px;
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

// Pushes the copy action to the far end of the toolbar without a spacer div.
const ToolbarEnd = styled.div`
  margin-left: auto;
`;

const MiniButton = styled.button`
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 5px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  color: ${({ theme }) => theme.themeColors.secondaryText};
  font-size: 11px;
  font-family: monospace;
  padding: 3px 9px;
  cursor: pointer;

  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
    color: ${({ theme }) => theme.themeColors.buttonText};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -2px;
  }
`;

// ---- the listing ----
//
// A grid of (line number, fold control, code) rather than a `<pre>`, because those three have to
// stay in register when a line wraps. In a pre they cannot: the gutter would be part of the text,
// so a wrapped continuation would carry a fake number and a fake indent. As grid ROWS, each line's
// number sits at the top of its own row however many visual lines the code takes.
//
// The column sizing switches with wrapping, and has to. Wrapped, the code column is
// `minmax(0, 1fr)` and nothing overflows sideways. Unwrapped it is `max-content`, which makes the
// grid itself wider than the box so the box's own `overflow-x` has something to scroll — with
// `1fr` there the long lines would simply spill out past the rounded border.
const Code = styled.div`
  display: grid;
  grid-template-columns: ${({ $wrap }) =>
    $wrap ? 'max-content max-content minmax(0, 1fr)' : 'max-content max-content max-content'};
  align-content: start;
  max-height: 620px;
  overflow: auto;
  padding: 10px 0;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  background: ${({ theme }) => theme.themeColors.appBackground};
  font-family: monospace;
  font-size: 12.5px;
  line-height: 1.55;

  .t-key {
    color: ${({ theme }) => syntaxFor(theme).key};
    font-weight: 600;
  }
  .t-string {
    color: ${({ theme }) => syntaxFor(theme).string};
  }
  .t-number {
    color: ${({ theme }) => syntaxFor(theme).number};
  }
  .t-literal {
    color: ${({ theme }) => syntaxFor(theme).literal};
  }
  .t-punct {
    color: ${({ theme }) => syntaxFor(theme).punct};
  }
`;

const LineNo = styled.span`
  padding: 0 8px 0 12px;
  text-align: right;
  user-select: none;
  font-size: 11px;
  color: ${({ theme }) => theme.themeColors.secondaryText};
`;

// Always rendered, even for a line that cannot fold, so the code column starts at the same x on
// every row. An empty cell is what keeps the listing from jittering as folds open and close.
const FoldCell = styled.span`
  width: 18px;
  display: flex;
  justify-content: center;
`;

// The caret is drawn by CSS rather than rendered as a child, and that is not a style choice. The
// code region's text content has to BE the document — a selection dragged across it, or anything
// reading it, must not pick up a triangle between the line number and the brace. The button's
// aria-label already carries what the glyph means, so the glyph itself is decoration.
const FoldButton = styled.button`
  border: 0;
  padding: 0;
  width: 18px;
  background: transparent;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  font-size: 9px;
  line-height: inherit;
  cursor: pointer;

  &::before {
    content: '▼';
  }

  &[aria-expanded='false']::before {
    content: '▶';
  }

  &:hover {
    color: ${({ theme }) => theme.themeColors.text};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    outline-offset: -1px;
  }
`;

// The indent is padding rather than literal spaces so a wrapped line's continuation lines up under
// its own key instead of under the left margin.
const LineText = styled.span`
  padding-right: 14px;
  color: ${({ theme }) => theme.themeColors.text};
  white-space: ${({ $wrap }) => ($wrap ? 'pre-wrap' : 'pre')};
  overflow-wrap: ${({ $wrap }) => ($wrap ? 'anywhere' : 'normal')};
`;

const FoldedSummary = styled.span`
  margin: 0 5px;
  padding: 0 6px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  color: ${({ theme }) => theme.themeColors.secondaryText};
  font-size: 10.5px;
`;

const INDENT_CH = 2;

// A value that cannot be serialised is a bug somewhere upstream, not something to crash the page
// over — the pane says so and the rest of the record stays readable.
const stringify = (value) => {
  try {
    const text = JSON.stringify(value, null, 2);
    return text === undefined ? null : text;
  } catch (e) {
    return null;
  }
};

const sizeLabel = (text) => {
  const kb = text.length / 1024;
  return kb < 1 ? `${text.length} chars` : `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
};

const Tokens = ({ tokens }) =>
  tokens.map((token, i) => (
    <span key={i} className={`t-${token.t}`}>
      {token.v}
    </span>
  ));

/**
 * `views` — one or more named renderings of the same spec, e.g. the buffer on screen versus what
 * Save would send. Each is `{ key, label, caption, value }`. A view whose value is null/undefined
 * is dropped, so a caller can list one unconditionally and let it disappear when there is nothing
 * behind it. With a single view the switch is not rendered at all.
 */
export const SpecJsonPane = ({ views, defaultOpen = false, heading = 'The whole spec as JSON', testId }) => {
  const available = (views || []).filter((view) => view && view.value !== undefined && view.value !== null);

  const [isOpen, setIsOpen] = React.useState(!!defaultOpen);
  const [viewKey, setViewKey] = React.useState(available.length > 0 ? available[0].key : null);
  const [isWrapped, setIsWrapped] = React.useState(true);
  // Keyed by line index. Indices move when the buffer is edited, so a stale entry can only ever
  // fold the wrong block — never corrupt the listing — and Unfold all is one click away. Resetting
  // on every keystroke instead would make the pane useless to read while typing.
  const [folded, setFolded] = React.useState({});

  // A view can vanish under the selection — "Last saved" is only offered while the buffer is dirty,
  // so saving takes it away while it is the one being read. Falling back to the first view keeps the
  // pane showing a real document rather than an empty one.
  const view = available.find((v) => v.key === viewKey) || available[0];
  const text = view ? stringify(view.value) : null;

  // Memoised on the TEXT, not the value: the payload view builds a fresh object every render, so a
  // value-keyed memo would rebuild several hundred line objects on every keystroke in the editor
  // above. The string compares by content.
  const lines = React.useMemo(() => (isOpen && text !== null ? jsonLines(JSON.parse(text)) : []), [isOpen, text]);

  // A fold index means nothing once the listing is a different document. Keyed on the view actually
  // being SHOWN, not on the selection, so the fallback above resets the folds too.
  const shownKey = view ? view.key : null;
  React.useEffect(() => {
    setFolded({});
  }, [shownKey]);

  // Two panes never coexist today (a spec is either a draft or a frozen record), but a hardcoded id
  // is a trap for the day one does.
  const paneId = React.useId();
  const headingId = `${paneId}-heading`;
  const bodyId = `${paneId}-body`;

  if (available.length === 0) return null;

  const toggleFold = (index) => setFolded((current) => ({ ...current, [index]: !current[index] }));

  // Everything but the root. Folding the root too would collapse the whole listing to `{ … 8 keys }`,
  // which is a listing that shows nothing — the useful state is every section closed, one line each.
  const foldAll = () => {
    const next = {};
    lines.forEach((line, index) => {
      if (line.open && line.depth >= 1) next[index] = true;
    });
    setFolded(next);
  };

  // Walk the model, skipping the body of anything folded. The line NUMBERS keep coming from the
  // real index, so a fold reads as a gap in the numbering rather than as a shorter document.
  const visible = [];
  for (let i = 0; i < lines.length; ) {
    const line = lines[i];
    const isFolded = line.open && !!folded[i];
    visible.push({ line, index: i, isFolded });
    i = isFolded ? line.close + 1 : i + 1;
  }

  const lineCount = text === null ? 0 : text.split('\n').length;

  return (
    <Panel aria-labelledby={headingId}>
      <Head
        type="button"
        id={headingId}
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Caret $open={isOpen} aria-hidden="true">
          ▶
        </Caret>
        <HeadText>
          <Body5 bold>{heading}</Body5>
        </HeadText>
        {text !== null && (
          <Body7 isLowContrast>{`${lineCount} line${lineCount === 1 ? '' : 's'} · ${sizeLabel(text)}`}</Body7>
        )}
      </Head>

      {isOpen && (
        <Bodywrap id={bodyId}>
          <Container top={16}>
            <Toolbar>
              {available.length > 1 && (
                <ModeToggle role="group" aria-label="Which JSON to show">
                  {available.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      aria-pressed={option.key === view.key}
                      onClick={() => setViewKey(option.key)}
                    >
                      {option.label}
                    </button>
                  ))}
                </ModeToggle>
              )}
              <MiniButton type="button" onClick={foldAll}>
                Fold all
              </MiniButton>
              <MiniButton type="button" onClick={() => setFolded({})}>
                Unfold all
              </MiniButton>
              <MiniButton
                type="button"
                aria-pressed={isWrapped}
                onClick={() => setIsWrapped((wrapped) => !wrapped)}
                title="Wrap long lines — a prompt is a single JSON string and routinely runs past the edge"
              >
                Wrap
              </MiniButton>
              {text !== null && (
                <ToolbarEnd>
                  <CopyButton text={text} transparent />
                </ToolbarEnd>
              )}
            </Toolbar>
          </Container>

          {view.caption && (
            <Container top={10}>
              <Consequence>{view.caption}</Consequence>
            </Container>
          )}

          <Container top={12}>
            {text === null ? (
              <Consequence $tone="bad">
                This value could not be serialised to JSON, which should not be possible for a spec that came off the
                wire. Nothing is shown rather than something misleading.
              </Consequence>
            ) : (
              <Code
                $wrap={isWrapped}
                data-testid={testId}
                role="group"
                aria-label={`${view.label || heading} as JSON`}
                tabIndex={0}
              >
                {visible.map(({ line, index, isFolded }) => (
                  <React.Fragment key={index}>
                    <LineNo aria-hidden="true">{index + 1}</LineNo>
                    <FoldCell>
                      {line.open && (
                        <FoldButton
                          type="button"
                          aria-expanded={!isFolded}
                          aria-label={`${isFolded ? 'Unfold' : 'Fold'} lines ${index + 1} to ${line.close + 1}`}
                          onClick={() => toggleFold(index)}
                        />
                      )}
                    </FoldCell>
                    <LineText $wrap={isWrapped} style={{ paddingLeft: `${line.depth * INDENT_CH}ch` }}>
                      <Tokens tokens={line.tokens} />
                      {isFolded && (
                        <>
                          <FoldedSummary>{`… ${line.summary}`}</FoldedSummary>
                          <span className="t-punct">{line.closeText}</span>
                        </>
                      )}
                    </LineText>
                  </React.Fragment>
                ))}
              </Code>
            )}
          </Container>
        </Bodywrap>
      )}
    </Panel>
  );
};
